import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { testimonialsAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import { RichTextEditor } from '../ui/rich-text-editor';

const TestimonialsTab = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testForm, setTestForm] = useState({ name: '', role: '', company: '', text: '', avatar: '', rating: 5 });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await testimonialsAPI.getAll();
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle testimonianze');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testForm.avatar) {
      toast.error('Carica un avatar per la testimonianza');
      return;
    }
    try {
      if (editingTest) {
        await testimonialsAPI.update(editingTest.id, testForm);
        toast.success('Testimonianza aggiornata!');
      } else {
        await testimonialsAPI.create(testForm);
        toast.success('Testimonianza aggiunta!');
      }
      loadTestimonials();
      setIsDialogOpen(false);
      setEditingTest(null);
      setTestForm({ name: '', role: '', company: '', text: '', avatar: '', rating: 5 });
    } catch (error) {
      toast.error('Errore nel salvare la testimonianza');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa testimonianza?')) return;
    try {
      await testimonialsAPI.delete(id);
      toast.success('Testimonianza eliminata!');
      loadTestimonials();
    } catch (error) {
      toast.error('Errore nell\'eliminare la testimonianza');
    }
  };

  const handleEdit = (test) => {
    setEditingTest(test);
    setTestForm({
      name: test.name,
      role: test.role,
      company: test.company,
      text: test.text,
      avatar: test.avatar,
      rating: test.rating
    });
    setIsDialogOpen(true);
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Gestione Testimonianze</CardTitle>
            <CardDescription>Aggiungi, modifica o elimina testimonianze</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-testimonial-btn"
                onClick={() => { 
                  setEditingTest(null); 
                  setTestForm({ name: '', role: '', company: '', text: '', avatar: '', rating: 5 }); 
                }}
              >
                <Plus className="w-4 h-4 mr-2" />Aggiungi Testimonianza
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTest ? 'Modifica Testimonianza' : 'Nuova Testimonianza'}</DialogTitle>
                <DialogDescription>{editingTest ? 'Aggiorna le informazioni' : 'Compila i dettagli'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input 
                    id="name" 
                    data-testid="testimonial-name-input"
                    value={testForm.name} 
                    onChange={(e) => setTestForm({ ...testForm, name: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="role">Ruolo</Label>
                  <Input 
                    id="role" 
                    data-testid="testimonial-role-input"
                    value={testForm.role} 
                    onChange={(e) => setTestForm({ ...testForm, role: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="company">Azienda/Ospedale</Label>
                  <Input 
                    id="company" 
                    data-testid="testimonial-company-input"
                    value={testForm.company} 
                    onChange={(e) => setTestForm({ ...testForm, company: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="text">Testimonianza</Label>
                  <div className="mt-2">
                    <RichTextEditor
                      value={testForm.text}
                      onChange={(value) => setTestForm({ ...testForm, text: value })}
                      placeholder="Scrivi la testimonianza..."
                      minHeight="150px"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="rating">Valutazione (1-5)</Label>
                  <Input 
                    id="rating" 
                    type="number" 
                    min="1" 
                    max="5" 
                    data-testid="testimonial-rating-input"
                    value={testForm.rating} 
                    onChange={(e) => setTestForm({ ...testForm, rating: parseInt(e.target.value) })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label>Avatar</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={testForm.avatar}
                      onChange={(url) => setTestForm({ ...testForm, avatar: url })}
                      uploadType="image"
                      label="Carica avatar"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="submit-testimonial-btn">{editingTest ? 'Aggiorna' : 'Crea'} Testimonianza</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Avatar</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Azienda</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : testimonials.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nessuna testimonianza</TableCell></TableRow>
              ) : (
                testimonials.map((test) => (
                  <TableRow key={test.id} data-testid={`testimonial-row-${test.id}`}>
                    <TableCell><img src={test.avatar} alt={test.name} className="w-12 h-12 rounded-full object-cover" /></TableCell>
                    <TableCell className="font-medium">{test.name}</TableCell>
                    <TableCell>{test.role}</TableCell>
                    <TableCell>{test.company}</TableCell>
                    <TableCell>
                      <div className="flex">
                        {Array.from({ length: test.rating }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(test)} data-testid={`edit-testimonial-${test.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(test.id)} className="text-destructive hover:text-destructive" data-testid={`delete-testimonial-${test.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TestimonialsTab;
